import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { isDefined } from "@clipboard-health/util-ts";
import { useState } from "react";

import { useGetRosterWindowSettings } from "../api/useGetRosterWindowSettings";
import { useUpdateRosterWindowSettings } from "../api/useUpdateRosterWindowSettings";
import { type RosterWindowRules } from "@clipboard-health/contract-backend-main";
import {
  RosterSettingsEditorErrorView,
  RosterSettingsEditorLoadingView,
  RosterSettingsEditorView,
} from "./RosterSettingsEditorView";

interface RosterSettingsEditorProps {
  workplaceId: string;
}

type RosterWindowThresholds = Pick<
  RosterWindowRules,
  "longLeadThresholdDays" | "urgentThresholdDays"
>;

export function RosterSettingsEditor(props: RosterSettingsEditorProps) {
  const { workplaceId } = props;
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();
  const isClipboardRosterEnabled = useCbhFlag(CbhFeatureFlag.CLIPBOARD_ROSTER_ENABLED, {
    defaultValue: false,
  });
  const { enabled: isRosterWindowRedesignEnabled } = useCbhFlag(
    CbhFeatureFlag.ROSTER_WINDOW_REDESIGN_ENABLED,
    { defaultValue: { enabled: false } }
  );
  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useGetRosterWindowSettings({
    workplaceId,
    enabled: isClipboardRosterEnabled && isRosterWindowRedesignEnabled,
  });
  const updateSettings = useUpdateRosterWindowSettings();
  const [draftThresholds, setDraftThresholds] = useState<RosterWindowThresholds>();

  if (!isClipboardRosterEnabled || !isRosterWindowRedesignEnabled) {
    return null;
  }

  if (isLoading) {
    return <RosterSettingsEditorLoadingView />;
  }

  if (isError || !settings) {
    return <RosterSettingsEditorErrorView onRetry={() => void refetch()} />;
  }

  const displayedThresholds = isDefined(draftThresholds) ? draftThresholds : settings;

  const handleFieldChange = (key: keyof RosterWindowRules, value: number) => {
    const isThresholdChange = key === "longLeadThresholdDays" || key === "urgentThresholdDays";
    const currentThresholds = isThresholdChange ? displayedThresholds : settings;
    const nextAttributes: RosterWindowRules = {
      longLeadThresholdDays: currentThresholds.longLeadThresholdDays,
      longLeadOpenDaysBeforeStart: settings.longLeadOpenDaysBeforeStart,
      shortLeadOpenDaysAfterPosting: settings.shortLeadOpenDaysAfterPosting,
      urgentThresholdDays: currentThresholds.urgentThresholdDays,
      [key]: value,
    };

    if (
      isThresholdChange &&
      nextAttributes.urgentThresholdDays > nextAttributes.longLeadThresholdDays
    ) {
      setDraftThresholds({
        longLeadThresholdDays: nextAttributes.longLeadThresholdDays,
        urgentThresholdDays: nextAttributes.urgentThresholdDays,
      });
      return;
    }

    if (isThresholdChange) {
      setDraftThresholds(undefined);
    }

    const isUnchanged =
      nextAttributes.longLeadThresholdDays === settings.longLeadThresholdDays &&
      nextAttributes.longLeadOpenDaysBeforeStart === settings.longLeadOpenDaysBeforeStart &&
      nextAttributes.shortLeadOpenDaysAfterPosting === settings.shortLeadOpenDaysAfterPosting &&
      nextAttributes.urgentThresholdDays === settings.urgentThresholdDays;
    if (isUnchanged) {
      return;
    }

    updateSettings.mutate(
      { workplaceId, attributes: nextAttributes },
      {
        onSuccess: () => {
          showSuccessToast(tu("Roster settings saved."));
        },
        onError: () => {
          showErrorToast(tu("Failed to save roster settings. Please try again."));
        },
      }
    );
  };

  const handleToggleShiftCancellation = (checked: boolean) => {
    updateSettings.mutate(
      { workplaceId, attributes: { allowShiftCancellation: checked } },
      {
        onSuccess: () => {
          showSuccessToast(tu("Shift cancellation setting updated."));
        },
        onError: () => {
          showErrorToast(tu("Failed to update shift cancellation setting. Please try again."));
        },
      }
    );
  };

  return (
    <RosterSettingsEditorView
      settings={{
        longLeadThresholdDays: displayedThresholds.longLeadThresholdDays,
        longLeadOpenDaysBeforeStart: settings.longLeadOpenDaysBeforeStart,
        shortLeadOpenDaysAfterPosting: settings.shortLeadOpenDaysAfterPosting,
        urgentThresholdDays: displayedThresholds.urgentThresholdDays,
      }}
      allowShiftCancellation={settings.allowShiftCancellation}
      isSaving={updateSettings.isLoading}
      thresholdError={
        isDefined(draftThresholds)
          ? tu("Urgent threshold cannot be greater than the long-lead threshold.")
          : undefined
      }
      onChange={handleFieldChange}
      onToggleShiftCancellation={handleToggleShiftCancellation}
    />
  );
}
